/* ============================================
   NeonShrine Theme — Core JavaScript
   ============================================ */

(function() {
  'use strict';

  /* --- Theme Toggle --- */
  const ThemeToggle = {
    init() {
      const toggleBtns = document.querySelectorAll('[data-theme-toggle]');
      toggleBtns.forEach(btn => {
        btn.addEventListener('click', () => this.toggle());
      });
    },

    toggle() {
      const current = document.documentElement.getAttribute('data-theme') || 'dark';
      const next = current === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('neonshrine-theme', next);

      document.querySelectorAll('[data-theme-toggle]').forEach(btn => {
        const sunIcon = btn.querySelector('.theme-toggle__sun');
        const moonIcon = btn.querySelector('.theme-toggle__moon');
        if (sunIcon && moonIcon) {
          sunIcon.style.display = next === 'dark' ? 'none' : 'block';
          moonIcon.style.display = next === 'dark' ? 'block' : 'none';
        }
      });
    }
  };

  /* --- Scroll to Top --- */
  const ScrollToTop = {
    init() {
      const btn = document.getElementById('scroll-to-top');
      if (!btn) return;

      window.addEventListener('scroll', () => {
        if (window.scrollY > 400) {
          btn.classList.add('is-visible');
        } else {
          btn.classList.remove('is-visible');
        }
      }, { passive: true });

      btn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  };

  /* --- Promo Banner Dismiss --- */
  const PromoBanner = {
    init() {
      const banner = document.getElementById('promo-banner');
      const closeBtn = banner ? banner.querySelector('[data-promo-close]') : null;
      if (!banner || !closeBtn) return;

      if (sessionStorage.getItem('neonshrine-promo-dismissed')) {
        banner.remove();
        return;
      }

      closeBtn.addEventListener('click', () => {
        banner.style.height = banner.offsetHeight + 'px';
        requestAnimationFrame(() => {
          banner.style.transition = 'height var(--duration-normal) var(--ease-smooth), opacity var(--duration-normal) var(--ease-smooth)';
          banner.style.height = '0';
          banner.style.opacity = '0';
          banner.style.overflow = 'hidden';
        });
        sessionStorage.setItem('neonshrine-promo-dismissed', '1');
        setTimeout(() => banner.remove(), 300);
      });
    }
  };

  /* --- Mobile Menu --- */
  const MobileMenu = {
    init() {
      const openBtn = document.querySelector('[data-mobile-menu-open]');
      const closeBtn = document.querySelector('[data-mobile-menu-close]');
      const menu = document.getElementById('mobile-menu');
      const overlay = document.getElementById('overlay');
      if (!menu) return;

      const open = () => {
        menu.classList.add('is-open');
        menu.setAttribute('aria-hidden', 'false');
        if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
        if (overlay) overlay.classList.add('is-active');
        document.body.style.overflow = 'hidden';
        const firstLink = menu.querySelector('a, button');
        if (firstLink) firstLink.focus();
      };

      const close = (returnFocus = true) => {
        menu.classList.remove('is-open');
        menu.setAttribute('aria-hidden', 'true');
        if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
        if (overlay) overlay.classList.remove('is-active');
        document.body.style.overflow = '';
        if (returnFocus && openBtn) openBtn.focus();
      };

      if (openBtn) openBtn.addEventListener('click', open);
      if (closeBtn) closeBtn.addEventListener('click', close);
      if (overlay) overlay.addEventListener('click', close);

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && menu.classList.contains('is-open')) {
          close();
        }
      });

      // Navigating from the drawer: slide it closed and fade the page out so
      // the transition to the next page reads as intentional instead of the
      // menu freezing open until the new document paints.
      menu.addEventListener('click', (e) => {
        const link = e.target.closest('a[href]');
        if (!link) return;
        if (e.defaultPrevented || e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (link.target === '_blank' || link.origin !== window.location.origin) return;

        close(false);

        // Same-page anchor links don't trigger a page load, so skip the fade
        // (it would leave the page stuck at opacity 0).
        const samePage = link.pathname === window.location.pathname &&
          link.search === window.location.search && link.hash;
        if (samePage) return;

        document.documentElement.classList.add('is-leaving');
        // Failsafe: if navigation is aborted (offline, user cancels), bring
        // the page back rather than leaving it faded out.
        setTimeout(() => {
          document.documentElement.classList.remove('is-leaving');
        }, 5000);
      });

      // Restore state when the page is served from the back/forward cache.
      window.addEventListener('pageshow', () => {
        document.documentElement.classList.remove('is-leaving');
        if (menu.classList.contains('is-open')) close(false);
      });
    }
  };

  /* --- Toast notifications --- */
  const Toast = {
    el: null,
    timer: null,

    init() {
      this.el = document.getElementById('toast');
      /* Other scripts (e.g. wishlist.js) can request a toast via this event */
      document.addEventListener('toast:show', (e) => {
        if (e.detail && e.detail.message) this.show(e.detail.message, e.detail);
      });
    },

    /* message is theme-authored text, opts.href/link render a trailing link */
    show(message, opts = {}) {
      if (!this.el) return;
      const link = opts.href
        ? `<a href="${opts.href}" class="toast__link">${opts.linkLabel || 'View'}</a>`
        : '';
      this.el.innerHTML = `<span class="toast__check" aria-hidden="true">\u2713</span><span class="toast__msg">${message}</span>${link}`;
      this.el.hidden = false;
      this.el.classList.remove('is-visible');
      void this.el.offsetWidth; /* restart the slide-in animation */
      this.el.classList.add('is-visible');
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.hide(), 4000);
    },

    hide() {
      if (!this.el) return;
      this.el.classList.remove('is-visible');
      clearTimeout(this.timer);
      this.timer = setTimeout(() => { this.el.hidden = true; }, 300);
    }
  };

  /* --- Cart count badge --- */
  const CartCount = {
    update(count) {
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    },

    async refresh() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        this.update(cart.item_count);
        return cart;
      } catch (e) {
        return null;
      }
    }
  };

  /* --- Cart page: AJAX quantity/remove --- */
  const CartPage = {
    init() {
      const page = document.querySelector('.cart-page');
      if (!page) return;
      const sectionEl = page.closest('.shopify-section');
      this.sectionId = sectionEl ? sectionEl.id.replace('shopify-section-', '') : null;

      /* Delegated so the re-rendered section needs no re-binding */
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('.cart-page form[action*="/cart/change"]');
        if (!form || !this.sectionId) return; /* native POST fallback */
        e.preventDefault();

        const idInput = form.querySelector('input[name="id"]');
        const qtyInput = form.querySelector('input[name="quantity"]');
        if (!idInput || !qtyInput) return form.submit();

        const btn = form.querySelector('button');
        if (btn) btn.disabled = true;

        try {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: idInput.value,
              quantity: parseInt(qtyInput.value, 10),
              sections: this.sectionId
            })
          });
          if (!res.ok) throw new Error('Cart change failed: ' + res.status);
          const data = await res.json();
          CartCount.update(data.item_count);

          const html = data.sections && data.sections[this.sectionId];
          if (html) {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('.cart-page');
            const current = document.querySelector('.cart-page');
            if (fresh && current) current.replaceWith(fresh);
          }
        } catch (err) {
          console.error(err);
          form.submit(); /* graceful full-reload fallback */
        }
      });
    }
  };

  /* --- Add to Cart --- */
  const AddToCart = {
    init() {
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('form[action="/cart/add"]');
        if (!form) return;
        e.preventDefault();

        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Adding...';
        }

        try {
          const formData = new FormData(form);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (res.ok) {
            document.dispatchEvent(new CustomEvent('cart:add'));
            CartCount.refresh();
            Toast.show('Added to cart', { href: '/cart', linkLabel: 'View cart' });
          }
        } catch (e) {
          console.error('Add to cart failed:', e);
        } finally {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = submitBtn.getAttribute('data-add-text') || 'Add to Cart';
          }
        }
      });
    }
  };

  /* --- Init all modules --- */
  document.addEventListener('DOMContentLoaded', () => {
    ThemeToggle.init();
    ScrollToTop.init();
    PromoBanner.init();
    MobileMenu.init();
    Toast.init();
    CartPage.init();
    AddToCart.init();
  });
})();
